import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Switch as MuiSwitch, FormControlLabel } from '@mui/material';
import { Button } from '../button/Button.component';

const StyledFormControlLabel = styled(FormControlLabel)`
  align-items: center;
  color: #333;
  justify-content: space-between;
  margin: 0;
  width: ${({ labelPlacement }) => (labelPlacement === 'start' ? '100%' : 'auto')};
  & .MuiFormControlLabel-label {
    padding-right: ${({ labelPlacement }) => (labelPlacement === 'start' ? '10px' : '0')};
    padding-left: ${({ labelPlacement }) => (labelPlacement === 'end' ? '10px' : '0')};
  }
  & .MuiFormControlLabel-label.Mui-disabled {
    color: ${({ theme }) => theme.palette.text.disabled};
  }
`;

const ButtonWrapper = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
  & button {
    color: ${({ disabled, theme }) => (disabled ? theme.palette.text.disabled : theme.palette.primary.main)};
    font-weight: 600;
    height: inherit;
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    margin: 0 0 0 10px;
    border: none;
  }
`;

const StyledSwitch = styled(MuiSwitch)`
  height: 22px;
  padding: 1px;
  width: 38px;
  &:focus-within {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.palette.primary[50]};
    outline: solid transparent 1px;
  }
  & .MuiSwitch-track {
    background-color: ${({ theme }) => theme.palette.grey[80]};
    border-radius: 18px;
    opacity: 1 !important;
    @media (prefers-contrast: more) {
      border: 1px solid;
      border-color: buttonText;
    }
  }
  & .MuiSwitch-thumb {
    box-shadow: none;
    height: 16px;
    width: 16px;
    @media (prefers-contrast: more) {
      background-color: buttonText;
    }
  }
  & .MuiSwitch-switchBase {
    padding: 3px;
  }
  & .Mui-disabled .MuiSwitch-thumb {
    background-color: ${({
    checked,
    disabled,
    theme,
  }) => (!checked && disabled ? theme.palette.grey[60] : theme.palette.common.white)};
  }
  & .MuiSwitch-switchBase.Mui-checked {
    color: ${({ theme }) => theme.palette.common.white};
    transform: translateX(16px);
  }
  & .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track {
   background-color: ${({ theme }) => theme.palette.grey[5]};
    @media (prefers-contrast: more) {
      background-color: ${({ theme }) => theme.palette.grey[80]};
    }
  
  }
  & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
    background-color: ${({ disabled, theme }) => (disabled ? theme.palette.primary[20] : theme.palette.primary.main)};
  }
`;

/**
* Renders a <Switch /> component with optional label.
* @param {Object} props
* @param {string} props.id - the id attribute of the input element.
* @param {string} props.label - the text of the input label.
* @param {string} [props.labelPlacement='start'] - determines the alignment of the label;
* valid values are 'start', 'end', 'top', 'bottom'.
* @param {string} props.name - the name attribute of the input element.
* @param {Function} props.onChange - the callback function when the switch is clicked.
* @param {Function} props.onClick - the callback function when the (optional) button label is clicked.
* @param {string} props.dataTestIdPrefix - prefix value used for data-testid.
* @param {string} [props.switchType='default'] - the type of switch;
* valid values are 'default', 'button' (label is also a button), or 'standalone' (label is elsewhere).
* @param {string|number} props.value - the value attribute of the input element.
* @param {boolean} [props.checked=false] - determines whether the checkbox is checked.
* @param {boolean} [props.disabled=false] - determines whether the checkbox is active.
*/
export const Switch = forwardRef(({
  disabled = false,
  id,
  label = '',
  labelPlacement = 'start',
  name,
  dataTestIdPrefix = 'Switch',
  onChange,
  onClick = () => {},
  switchType = 'default',
  inputProps = {},
  ...props
}, forwardedRef) => {
  const pureSwitch = (
    <StyledSwitch
      disableRipple
      disabled={disabled}
      id={`${id}Switch`}
      data-testid={`${dataTestIdPrefix}_${id}Switch`}
      name={name}
      onChange={onChange}
      ref={forwardedRef}
      {...props}
      inputProps={{
        'data-testid': `${dataTestIdPrefix}_${id}Input`,
        'aria-label': label,
        'aria-checked': props.checked,
        role: props.role === 'group' || inputProps.role === 'menuitemcheckbox' ? 'menuitemcheckbox' : 'checkbox',
        ...inputProps,
      }}
    />
  );

  return (
    <>
      {switchType === 'default' && (
        <StyledFormControlLabel
          data-testid={`${dataTestIdPrefix}_${id}Label`}
          label={label}
          disabled={disabled}
          control={pureSwitch}
          labelPlacement={labelPlacement}
        />
      )}
      {switchType === 'button' && (
        <ButtonWrapper disabled={disabled} role="group">
          {pureSwitch}
          <Button
            id={`${id}Button`}
            data-testid={`${dataTestIdPrefix}_${id}Button`}
            disabled={disabled}
            onClick={onClick}
            variant="textPrimary"
            role="menuitem"
          >
            {label}
          </Button>
        </ButtonWrapper>
      )}
      {switchType === 'standalone' && pureSwitch}
    </>
  );
});

Switch.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  labelPlacement: PropTypes.oneOf(['start', 'end', 'top', 'bottom']),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClick: PropTypes.func,
  dataTestIdPrefix: PropTypes.string,
  switchType: PropTypes.oneOf(['default', 'button', 'standalone']),
  inputProps: PropTypes.shape({}),
};
